import React, { useMemo } from "react";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { Account } from "@common/types/domains/accounting/account";
import { displayAccountLabel } from "@common/helpers/accounting";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useAccounts } from "@hooks/useAccounts";

interface Props {
    entityId: number;
    accountClass: AccountClass;
    selectedAccount?: Account;
    onChange: (account: Account | null) => void;
    excludeIds?: string[];
}

export const MergeTargetAccountCombobox: React.FC<Props> = ({
    entityId,
    accountClass,
    selectedAccount,
    onChange,
    excludeIds,
}) => {
    const { accounts, isLoading } = useAccounts({
        entityId,
        classes: [accountClass],
    });

    const selectableAccounts = useMemo(
        () => accounts.filter((account) => !excludeIds?.includes(account.id)),
        [accounts, excludeIds],
    );
    const accountsById = useMemo(
        () =>
            new Map(selectableAccounts.map((account) => [account.id, account])),
        [selectableAccounts],
    );
    const items = useMemo<Array<PickerItemData<string>>>(
        () =>
            selectableAccounts.map((account) => ({
                value: account.id,
                label: displayAccountLabel(account, true),
            })),
        [selectableAccounts],
    );

    return (
        <Picker.Select
            items={items}
            searchable
            searchPlaceholder="Search accounts…"
            emptyLabel="No matching accounts"
            status={isLoading ? "loading" : "idle"}
            loadingLabel="Loading…"
            value={selectedAccount?.id ?? null}
            onValueChange={(id) =>
                onChange(id === null ? null : (accountsById.get(id) ?? null))
            }
            placeholder="Select account"
            renderValue={() =>
                selectedAccount
                    ? displayAccountLabel(selectedAccount, true)
                    : undefined
            }
            trigger={<Picker.Button variant="primary" size="xl" block />}
        />
    );
};
